/**
 * Hard ceiling on any file POSTed through the JobQ API, and the honest way to
 * explain it to whoever picked the file.
 *
 * jobq-ui sends files as raw multipart to API Gateway (HTTP API `du5ppqj4e0`),
 * which base64-encodes the body before invoking the Lambda. Lambda's synchronous
 * request payload cap is 6 MiB and base64 inflates by 4/3, so the real ceiling on
 * the file itself is 6 MiB * 3/4, less the multipart envelope.
 *
 * Over that line API Gateway returns a bare 413 that carries NO
 * `access-control-allow-origin` header — this API has no gateway-level CORS
 * config, and the FastAPI CORS middleware never runs because the integration is
 * never invoked. The browser therefore blocks the response and fetch() rejects
 * with `TypeError: Failed to fetch`, which is indistinguishable from the machine
 * being offline. That is why an oversized DXF used to surface as "Unable to
 * connect to the server. Please check your internet connection and try again."
 * and left nothing at all in the Lambda logs.
 *
 * Measured exactly against prod on 2026-08-27:
 *   4,641,956 B -> 200
 *   4,744,356 B -> 413 (no ACAO header)
 *
 * So: check the size here, before the request is ever made, and tell the user
 * the real reason instead of blaming their network.
 */

const LAMBDA_SYNC_PAYLOAD_BYTES = 6 * 1024 * 1024; // Lambda's hard synchronous cap
const BASE64_OVERHEAD = 4 / 3;                     // API Gateway encodes the body
const MULTIPART_ENVELOPE_BYTES = 64 * 1024;        // boundary, headers, pin/name/description fields

export const MAX_UPLOAD_BYTES =
  Math.floor(LAMBDA_SYNC_PAYLOAD_BYTES / BASE64_OVERHEAD) - MULTIPART_ENVELOPE_BYTES;

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "unknown size";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Returns null when the file fits, or a ready-to-show explanation when it does
 * not. Callers should refuse to send rather than letting the request fail
 * opaquely at the gateway.
 */
export function checkUploadSize(file, what = "file") {
  if (!file || !Number.isFinite(file.size)) return null;
  if (file.size <= MAX_UPLOAD_BYTES) return null;

  const hint = /\.dxf$/i.test(file.name || "")
    ? "Re-save it as R12/ASCII keeping only the flat 2D profile — purging 3D solids, embedded images and unused blocks usually shrinks a bloated DXF by an order of magnitude."
    : "Compress it, or split it into smaller files, before uploading.";

  return (
    `This ${what} is ${formatBytes(file.size)}, over the ${formatBytes(MAX_UPLOAD_BYTES)} upload limit, ` +
    `so it is rejected before it reaches the server. ${hint}`
  );
}

/**
 * Message for an upload whose fetch() rejected outright. With checkUploadSize()
 * guarding the pick, this is usually a genuinely lost connection — but a file
 * close to the limit is refused in exactly the same silent way, so say both
 * rather than asserting the network is at fault.
 */
export function uploadFailureMessage(file) {
  const size = file && Number.isFinite(file.size) ? ` (${formatBytes(file.size)} file)` : "";
  return (
    `The upload failed before it reached the server${size}. This is usually a dropped connection — ` +
    `but a file near the ${formatBytes(MAX_UPLOAD_BYTES)} limit is refused the same silent way. ` +
    `Try again, and if it keeps failing, send a smaller file.`
  );
}
